// Voice-to-voice "call mode" listening. Abstracts two speech-to-text paths behind
// one interface so Converse can run a continuous spoken loop:
//   • browser  — webkitSpeechRecognition, free + local (desktop Chrome/Edge).
//   • whisper  — record audio + POST /api/stt → Whisper. Fallback for platforms
//                without browser recognition (notably iOS Safari).
// A turn ends after ~1.3s of silence. The mic only runs in the listening phase
// (the caller stops it while the twin thinks/speaks) so the twin's own voice is
// never transcribed back.

import { BCP47 } from './tts.js';
import type { Language } from './prefs.js';

const SILENCE_MS = 1300;

export interface CallListener {
  supported: boolean;
  mode: 'browser' | 'whisper';
  start: () => void;
  stop: () => void;
}

interface Opts {
  language: Language;
  onResult: (text: string) => void;
  onError: (code: 'no_stt' | 'mic' | 'network' | 'stt_failed') => void;
}

function srCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike; webkitSpeechRecognition?: new () => SpeechRecognitionLike };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
interface SpeechRecognitionLike {
  lang: string; continuous: boolean; interimResults: boolean;
  start(): void; stop(): void; abort(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }> }) => void) | null;
  onerror: (() => void) | null; onend: (() => void) | null;
}

function browserListener({ language, onResult }: Opts): CallListener {
  const Ctor = srCtor();
  let rec: SpeechRecognitionLike | null = null;
  let active = false;
  let silence: ReturnType<typeof setTimeout> | null = null;
  let acc = '';
  const finalize = () => { const t = acc.trim(); acc = ''; if (t) onResult(t); };
  return {
    supported: Boolean(Ctor),
    mode: 'browser',
    start() {
      if (!Ctor) return;
      active = true; acc = '';
      rec = new Ctor();
      rec.lang = BCP47[language]; rec.continuous = true; rec.interimResults = true;
      rec.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i]; if (r?.isFinal) acc += ' ' + (r[0]?.transcript ?? '');
        }
        if (silence) clearTimeout(silence);
        silence = setTimeout(finalize, SILENCE_MS); // any speech (incl. interim) resets the silence window
      };
      rec.onerror = () => {};
      rec.onend = () => { if (active && rec) { try { rec.start(); } catch { /* already started */ } } };
      try { rec.start(); } catch { /* ignore */ }
    },
    stop() {
      active = false;
      if (silence) { clearTimeout(silence); silence = null; }
      try { rec?.abort(); } catch { /* ignore */ }
      rec = null; acc = '';
    },
  };
}

function pickMime(): string {
  const cand = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  for (const m of cand) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ } }
  return '';
}

function whisperListener({ language, onResult, onError }: Opts): CallListener {
  let active = false;
  let stream: MediaStream | null = null;
  let ctx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let rec: MediaRecorder | null = null;
  let raf = 0;
  let chunks: Blob[] = [];
  let speaking = false, silenceStart = 0, speechStart = 0;
  const THRESH = 0.012, SIL = SILENCE_MS, MIN_SPEECH = 300;

  async function transcribe(blob: Blob) {
    try {
      const token = localStorage.getItem('mdai_token');
      const r = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/stt?lang=${language}`, {
        method: 'POST',
        headers: { 'Content-Type': blob.type || 'audio/webm', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: blob,
      });
      if (r.status === 409) { onError('no_stt'); active = false; return; }
      if (!r.ok) { onError('stt_failed'); return; }
      const j = (await r.json()) as { text?: string };
      const t = (j.text ?? '').trim();
      if (t) onResult(t);
    } catch { onError('network'); }
  }

  function startSegment() {
    if (!stream) return;
    chunks = []; speaking = false; silenceStart = 0;
    const mime = pickMime();
    rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = async () => {
      const blob = new Blob(chunks, { type: rec?.mimeType || 'audio/webm' });
      if (active && blob.size > 1400) await transcribe(blob);
      if (active) startSegment();
    };
    try { rec.start(); } catch { /* ignore */ }
  }

  async function begin() {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const src = ctx.createMediaStreamSource(stream);
    analyser = ctx.createAnalyser(); analyser.fftSize = 512; src.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    startSegment();
    const tick = () => {
      if (!active || !analyser) return;
      analyser.getByteTimeDomainData(data);
      let sum = 0; for (const v of data) { const x = (v - 128) / 128; sum += x * x; }
      const rms = Math.sqrt(sum / data.length);
      const now = performance.now();
      if (rms > THRESH) { if (!speaking) { speaking = true; speechStart = now; } silenceStart = 0; }
      else if (speaking) {
        if (!silenceStart) silenceStart = now;
        else if (now - silenceStart > SIL && now - speechStart > MIN_SPEECH) {
          if (rec && rec.state !== 'inactive') rec.stop(); // ends the segment → onstop transcribes
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  }

  return {
    supported: typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
    mode: 'whisper',
    start() { active = true; begin().catch(() => onError('mic')); },
    stop() {
      active = false;
      cancelAnimationFrame(raf);
      try { if (rec && rec.state !== 'inactive') rec.stop(); } catch { /* ignore */ }
      try { ctx?.close(); } catch { /* ignore */ }
      try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      rec = null; ctx = null; analyser = null; stream = null;
    },
  };
}

/** Pick the browser path when available (free), else Whisper recording. */
export function createCallListener(opts: Opts): CallListener {
  const browser = browserListener(opts);
  if (browser.supported) return browser;
  return whisperListener(opts);
}
